import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCelular } from '../hooks/useCelular';
import { useNavigate, useParams, Link } from 'react-router-dom';
import aviso, { textoDoErro, SEM_SERVIDOR } from '../lib/aviso';
import { LuArrowLeft, LuInfo, LuArrowRight, LuGrid2X2, LuMaximize, LuMinus, LuPlus, LuPencil, LuCheck } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import Dialog from '../components/Dialog';
import TrilhoCanvas, {
    etapasComTarefa, layoutPadrao, LARGURA_NO, nomeEtapa, ZOOM_MAX, ZOOM_MIN, layoutOu, novaChave,
    type EtapaEdit, type Layout, type Pos, type Selecao,
} from '../components/TrilhoCanvas';
import { ModeloTarefaService, type ConjuntoTrilhoDetalhe, type ModeloTarefa } from '../services/ModeloTarefaService';
import { ParametrosEmpresaService, type ParametrosEmpresa } from '../services/ParametrosEmpresaService';
import { TipoTarefaService, type TipoTarefa } from '../services/TarefaService';
import { TRILHOS_PRONTOS } from '../data/trilhosProntos';
import '../components/ListKit.css';
import './TrilhoQuadroPage.css';

/**
 * Configurações › Vendas › Tarefas padrão › o quadro
 * (docs/propostas/tarefas-padrao-quadro.html, direção B).
 *
 * A rota aceita duas coisas: o id de um trilho da empresa (`/tarefas-padrao/3`)
 * e um pronto da biblioteca (`/tarefas-padrao/p-venda-consultiva`), que abre em
 * leitura — é a decisão 5 ("Ver abre o próprio quadro", sem inventar uma segunda
 * forma de ler o mesmo trilho).
 */

/** As três etapas, sempre as três: a que não tem modelo entra desligada. */
const paraEdicao = (etapas: ModeloTarefa[]): EtapaEdit[] =>
    etapasComTarefa().map(statusId => {
        const m = etapas.find(e => e.statusId === statusId && e.ativo);
        if (!m) return { statusId, titulo: '', tipoTarefaId: null, prazoDiasUteis: 2, ativo: false, desfechos: [] };
        return { ...m, desfechos: m.desfechos.map(d => ({ ...d, uid: novaChave() })) };
    });

const paraApi = (etapas: EtapaEdit[]): ModeloTarefa[] =>
    etapas.map(e => ({
        ...e,
        titulo: e.titulo.trim(),
        desfechos: e.desfechos.map(({ uid: _uid, ...d }, i) => ({ ...d, rotulo: d.rotulo.trim(), ordem: i })),
    }));

const TrilhoQuadroPage = () => {
    const celular = useCelular();
    const { id = '' } = useParams();
    const navegar = useNavigate();
    const pronto = id.startsWith('p-') ? TRILHOS_PRONTOS.find(t => t.chave === id.slice(2)) : undefined;
    const somenteLeitura = !!pronto;

    const [conjunto, setConjunto] = useState<ConjuntoTrilhoDetalhe | null>(null);
    const [nome, setNome] = useState('');
    const [etapas, setEtapas] = useState<EtapaEdit[]>([]);
    const [layout, setLayout] = useState<Layout>({ ...layoutPadrao() });
    const [original, setOriginal] = useState('');
    const [tipos, setTipos] = useState<TipoTarefa[]>([]);
    const [parametros, setParametros] = useState<ParametrosEmpresa | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});
    const [selecao, setSelecao] = useState<Selecao>(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState<Pos>({ x: 0, y: 0 });
    const [renomeando, setRenomeando] = useState(false);
    const [nomeNovo, setNomeNovo] = useState('');

    const instantaneo = (n: string, e: EtapaEdit[], l: Layout) =>
        JSON.stringify({ n: n.trim(), e: paraApi(e), l });

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const [tt, p] = await Promise.all([
                TipoTarefaService.getAll().catch(() => [] as TipoTarefa[]),
                ParametrosEmpresaService.ler().catch(() => null),
            ]);
            setTipos(tt); setParametros(p);

            if (pronto) {
                const ed = paraEdicao(pronto.etapas);
                setNome(pronto.nome); setEtapas(ed); setLayout({ ...layoutPadrao() });
                setOriginal(instantaneo(pronto.nome, ed, layoutPadrao()));
                setConjunto(null);
                return;
            }

            const c = await ModeloTarefaService.buscarConjunto(Number(id));
            const ed = paraEdicao(c.etapas ?? []);
            const l = layoutOu(c.layout);
            setConjunto(c); setNome(c.nome); setEtapas(ed); setLayout(l);
            setOriginal(instantaneo(c.nome, ed, l));
            setErros({});
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar o trilho.'));
            navegar('/tarefas-padrao');
        } finally {
            setCarregando(false);
        }
    }, [id, pronto, navegar]);

    useEffect(() => { carregar(); }, [carregar]);

    const sujo = useMemo(
        () => !carregando && !somenteLeitura && instantaneo(nome, etapas, layout) !== original,
        [nome, etapas, layout, original, carregando, somenteLeitura]);

    useEffect(() => {
        if (!sujo) return;
        const aviso = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', aviso);
        return () => window.removeEventListener('beforeunload', aviso);
    }, [sujo]);

    /* Delete apaga o desfecho selecionado — a seta e a linha são a mesma coisa.
       Só quando o foco não está num campo, senão comeria o texto de quem digita. */
    useEffect(() => {
        if (somenteLeitura) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            if (selecao?.tipo !== 'desfecho') return;
            const foco = document.activeElement?.tagName;
            if (foco === 'INPUT' || foco === 'TEXTAREA') return;
            e.preventDefault();
            setEtapas(es => es.map(et => ({ ...et, desfechos: et.desfechos.filter(d => d.uid !== selecao.uid) })));
            setSelecao(null);
        };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [selecao, somenteLeitura]);

    /* ─── Validação (as regras do §7, medidas aqui para mostrar no campo) ─── */

    const validar = () => {
        const achados: Record<string, string> = {};
        let houve = false;
        const marcar = (chave: string, msg: string) => { achados[chave] = msg; houve = true; };

        for (const e of etapas) {
            if (!e.ativo) continue;
            if (!e.titulo.trim()) marcar(`t-${e.statusId}`, 'Informe o título da tarefa.');
            if (e.prazoDiasUteis < 0 || e.prazoDiasUteis > 60) marcar(`p-${e.statusId}`, 'De 0 a 60 dias úteis.');
            if (tipos.length > 0 && !e.tipoTarefaId) marcar(`tt-${e.statusId}`, 'Escolha o tipo.');
            if (e.desfechos.length === 0) marcar(`d-${e.statusId}`, 'Adicione ao menos um desfecho.');
            for (const d of e.desfechos) {
                if (!d.rotulo.trim()) marcar(`r-${d.uid}`, 'Informe o rótulo do desfecho.');
                if (d.acao === 'move') {
                    if (!d.statusDestinoId) marcar(`c-${d.uid}`, 'Escolha a etapa de destino.');
                    else if (d.statusDestinoId === e.statusId) marcar(`c-${d.uid}`, 'O destino não pode ser a própria etapa.');
                }
                if (d.acao === 'reschedule' && (!d.diasReagendamento || d.diasReagendamento < 1 || d.diasReagendamento > 60))
                    marcar(`c-${d.uid}`, 'De 1 a 60 dias úteis.');
            }
        }
        if (etapas.every(e => !e.ativo)) marcar('geral', 'Ligue ao menos uma etapa.');
        return { achados, houve };
    };

    const salvar = async () => {
        if (!conjunto) return;
        const { achados, houve } = validar();
        setErros(achados);
        if (houve) {
            /* O erro fica no campo, dentro da caixa. O toast nasceria em cima do
               próprio Salvar (o toaster mora no mesmo canto do cabeçalho). */
            setTimeout(() => document.querySelector('.tq-inp.has-error, .tq-sem.has-error')
                ?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 0);
            return;
        }
        setSalvando(true);
        try {
            const r = await ModeloTarefaService.salvarConjunto(conjunto.conjuntoId, {
                nome: nome.trim(),
                chaveBiblioteca: conjunto.chaveBiblioteca,
                layout: JSON.stringify(layout),
                etapas: paraApi(etapas),
            });
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível salvar o trilho.'); return; }
            aviso.ok(r.mensagem || 'Trilho salvo.');
            await carregar();
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const ativar = async () => {
        if (!conjunto) return;
        const r = await ModeloTarefaService.ativarConjunto(conjunto.conjuntoId);
        if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível ativar o trilho.'); return; }
        aviso.ok(r.mensagem || 'Trilho ativado.');
        await carregar();
    };

    /** "Usar este trilho": o pronto vira um trilho da empresa e abre para editar. */
    const usarPronto = async () => {
        if (!pronto) return;
        setSalvando(true);
        try {
            const r = await ModeloTarefaService.criarConjunto({
                nome: pronto.nome,
                chaveBiblioteca: pronto.chave,
                layout: JSON.stringify(layoutPadrao()),
                etapas: pronto.etapas,
            });
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível copiar o trilho.'); return; }
            aviso.ok('Trilho copiado para a sua empresa.');
            navegar(`/tarefas-padrao/${r.dados.conjuntoId}`);
        } finally {
            setSalvando(false);
        }
    };

    const organizar = () => { setLayout({ ...layoutPadrao() }); setPan({ x: 0, y: 0 }); setZoom(1); };

    /* Enquadra o que existe, inclusive o que foi arrastado para coordenada
       negativa: mede a caixa envolvente do desenho e leva o canto dela para o
       canto do quadro. */
    const ajustarATela = () => {
        const cv = document.querySelector('.tq-cv');
        const pos = Object.values(layout);
        if (!cv || pos.length === 0) return;
        const r = cv.getBoundingClientRect();
        const minX = Math.min(...pos.map(p => p.x));
        const minY = Math.min(...pos.map(p => p.y));
        const largura = Math.max(...pos.map(p => p.x + LARGURA_NO)) - minX;
        const altura = Math.max(...pos.map(p => p.y + 300)) - minY;
        const bruto = Math.min(1, (r.width - 40) / largura, (r.height - 40) / altura);
        const z = Math.max(ZOOM_MIN, Math.round(bruto * 20) / 20);
        setZoom(z);
        setPan({ x: 20 - minX * z, y: 20 - minY * z });
    };

    const nEtapas = etapas.filter(e => e.ativo).length;
    const nDesfechos = etapas.filter(e => e.ativo).reduce((s, e) => s + e.desfechos.length, 0);

    return (
        <div className="page-shell lk-screen tq-screen">
            <PageHeader
                title={nome || 'Trilho'}
                subtitle={somenteLeitura
                    ? 'Trilho pronto da Leme · leitura. Use uma cópia para editar.'
                    : `${conjunto?.ativo ? 'Trilho ativo' : 'Trilho inativo'} · ${nEtapas} ${nEtapas === 1 ? 'etapa' : 'etapas'} com tarefa · ${nDesfechos} ${nDesfechos === 1 ? 'desfecho' : 'desfechos'}`}
                actions={
                    <div className="lk-actions">
                        <Link className="lk-btn" to="/tarefas-padrao"><LuArrowLeft size={14} /> Modelos</Link>
                        {somenteLeitura ? (
                            <button type="button" className="lk-btn is-primary" onClick={usarPronto} disabled={salvando}>
                                {salvando ? 'Copiando…' : 'Usar este trilho'}
                            </button>
                        ) : (
                            <>
                                {conjunto && !conjunto.ativo && (
                                    <button type="button" className="lk-btn" onClick={ativar} disabled={salvando || sujo}
                                        title={sujo ? 'Salve antes de ativar' : undefined}>
                                        <LuCheck size={14} /> Ativar
                                    </button>
                                )}
                                <button type="button" className="lk-btn is-primary" onClick={salvar} disabled={!sujo || salvando}>
                                    {salvando ? 'Salvando…' : 'Salvar'}
                                </button>
                            </>
                        )}
                    </div>
                }
            />

            <div className="page-body tq-body">
                {parametros && !parametros.usaTarefasPadrao && !somenteLeitura && (
                    <div className="tq-ban">
                        <LuInfo size={15} />
                        <span>As tarefas padrão estão <b>desligadas</b> para esta empresa: nada nasce até ligar em Minhas empresas.</span>
                        <Link className="lk-btn" to="/gerenciar-empresa">Abrir Parâmetros <LuArrowRight size={13} /></Link>
                    </div>
                )}

                {/* Celular: o quadro não existe. Montar trilho é arrastar caixa e
                    puxar ligação, e nenhuma das duas coisas tem equivalente no
                    dedo — o mundo do canvas mede 1348px numa tela de 358. Decisão
                    de 30/09/2026: aqui se LÊ o trilho, em lista; montar continua
                    no computador. A barra de zoom e "Organizar" somem junto,
                    porque não há o que organizar numa lista. */}
                {!celular && (
                <div className="tq-bar">
                    {!somenteLeitura && (
                        <button type="button" className="lk-btn" onClick={() => { setNomeNovo(nome); setRenomeando(true); }}>
                            <LuPencil size={13} /> Renomear
                        </button>
                    )}
                    <button type="button" className="lk-btn" onClick={organizar} disabled={somenteLeitura}>
                        <LuGrid2X2 size={13} /> Organizar
                    </button>
                    <button type="button" className="lk-btn" onClick={ajustarATela}>
                        <LuMaximize size={13} /> Ajustar à tela
                    </button>
                    <span className="tq-bar-sp" />
                    {erros.geral && <span className="tq-bar-erro">{erros.geral}</span>}
                    {/* O gesto existe, mas ninguém descobre gesto sozinho: a dica
                        fica ao lado do controle visível, não no lugar dele. */}
                    <span className="tq-bar-dica">Ctrl + rolar para o zoom · rolar para mover</span>
                    <span className="tq-bar-n">{Math.round(zoom * 100)}%</span>
                    <span className="tq-zoom">
                        <button type="button" aria-label="Diminuir o zoom" onClick={() => setZoom(z => Math.max(ZOOM_MIN, z - 0.1))}>
                            <LuMinus size={12} />
                        </button>
                        <button type="button" aria-label="Aumentar o zoom" onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + 0.1))}>
                            <LuPlus size={12} />
                        </button>
                    </span>
                </div>
                )}

                {carregando ? (
                    <div className="tq-skel" />
                ) : celular ? (
                    <div className="tq-lista">
                        <p className="tq-lista-aviso">
                            <LuInfo size={14} />
                            <span>Este é o trilho como ele está. Para montar ou mudar as ligações, abra no computador.</span>
                        </p>
                        {etapas.map(etapa => (
                            <section key={etapa.statusId} className="tq-let">
                                <header className="tq-let-h">
                                    <span className="tq-let-etapa">{nomeEtapa(etapa.statusId)}</span>
                                    {!etapa.ativo && <span className="tq-let-off">desligada</span>}
                                </header>
                                <p className="tq-let-t">{etapa.titulo || 'Sem título'}</p>
                                <p className="tq-let-m">
                                    {etapa.tipoTarefaNome
                                        || tipos.find(t => t.tipoTarefaId === etapa.tipoTarefaId)?.nome
                                        || 'Sem tipo'}
                                    {' · '}
                                    {etapa.prazoDiasUteis} {etapa.prazoDiasUteis === 1 ? 'dia útil' : 'dias úteis'}
                                </p>
                                {etapa.desfechos.length > 0 && (
                                    <ul className="tq-let-d">
                                        {etapa.desfechos.map(d => (
                                            <li key={d.uid}>
                                                <span className="tq-let-d-r">{d.rotulo || 'Sem rótulo'}</span>
                                                {/* Por extenso: no quadro a seta mostra para onde vai, e aqui
                                                    não há seta. "move" sem destino é o que a etiqueta curta
                                                    do canvas diz, e sozinha não informa nada. */}
                                                <span className={`tq-let-d-a is-${d.acao}`}>
                                                    {d.acao === 'move'
                                                        ? (d.statusDestinoId
                                                            ? `vai para ${nomeEtapa(d.statusDestinoId)}`
                                                            : 'muda de etapa')
                                                        : d.acao === 'reschedule'
                                                            ? `reagenda em ${d.diasReagendamento ?? 1} ${(d.diasReagendamento ?? 1) === 1 ? 'dia' : 'dias'}`
                                                            : d.acao === 'lose'
                                                                ? `marca como ${nomeEtapa(6)}`
                                                                : 'encerra a tarefa'}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        ))}
                    </div>
                ) : (
                    <TrilhoCanvas
                        etapas={etapas}
                        layout={layout}
                        zoom={zoom}
                        pan={pan}
                        selecao={selecao}
                        tipos={tipos}
                        erros={erros}
                        somenteLeitura={somenteLeitura}
                        onLayout={setLayout}
                        onPan={setPan}
                        onZoom={setZoom}
                        onEtapas={setEtapas}
                        onSelecao={setSelecao}
                    />
                )}

                {/* A nota ensina os gestos do quadro — arrastar e puxar a ligação.
                    No celular não há quadro, então ela ensinaria o que não dá para
                    fazer; sobra a parte que continua verdadeira ali. */}
                <p className="tq-nota">
                    {celular ? (
                        <>
                            As caixas são as etapas do funil — não há etapa para criar. O tipo de cada tarefa sai
                            de <Link className="tq-link" to="/tipos-tarefa">Tipos de tarefa</Link>.
                        </>
                    ) : (
                        <>
                            As caixas são as etapas do funil — não há etapa para criar. Arraste para organizar, puxe da
                            bolinha de um desfecho até a etapa de destino, e {nomeEtapa(3)} e {nomeEtapa(6)} recebem seta
                            sem criar tarefa. O tipo de cada tarefa sai de <Link className="tq-link" to="/tipos-tarefa">Tipos de tarefa</Link>.
                        </>
                    )}
                </p>
            </div>

            <Dialog
                open={renomeando}
                onClose={() => setRenomeando(false)}
                title="Renomear trilho"
                confirmLabel="Renomear"
                cancelLabel="Cancelar"
                onConfirm={() => { setNome(nomeNovo.trim() || nome); setRenomeando(false); }}
            >
                <label className="tq-dlg-lab" htmlFor="tq-nome">Nome</label>
                <input id="tq-nome" className="tq-inp" type="text" maxLength={80} autoFocus
                    value={nomeNovo} onChange={e => setNomeNovo(e.target.value)} />
            </Dialog>
        </div>
    );
};

export default TrilhoQuadroPage;
